<script setup>
    import { ref, computed, watch } from 'vue'
    import Modal from '@/Components/Modal.vue'
    import StarIcon from '@/Components/Icons/StarIcon.vue'
    import CheckCircleIcon from '@/Components/Icons/CheckCircleIcon.vue'
    import axios from 'axios'

    const props = defineProps({
        isOpen: {
            type: Boolean,
            default: false,
        },
        characterId: {
            type: Number,
            default: -1,
        },
        isCustomCharacter: {
            type: Boolean,
            default: false,
        },
    })

    const emit = defineEmits(['close'])

    const selectedPackage = ref('quarterly')
    const isProcessing = ref(false)
    const isLoading = ref(false)
    const packages = ref([])
    const error = ref(null)

    // Fetch subscription settings from API
    const fetchSettings = async () => {
        try {
            isLoading.value = true
            error.value = null

            const response = await axios.get('/api/subscription-modal/settings')

            console.log('Subscription modal API response:', response.data)

            if (response.data.success && response.data.data) {
                const settings = response.data.data
                const packageList = []

                // Add button one if configured
                if (settings.button_one) {
                    packageList.push({
                        id: settings.button_one.id,
                        subscriptionId: settings.button_one.subscription_id,
                        name: settings.button_one.name,
                        price: settings.button_one.price,
                        totalPrice: settings.button_one.total_price,
                        billingDetails: settings.button_one.billing_details,
                        pricePerMonth: settings.button_one.price_per_month,
                        savingsText: settings.button_one.savings_text,
                        label: settings.button_one.label,
                        instantCredits: settings.button_one.instant_credits,
                        creditsPerDay: settings.button_one.credits_per_day,
                        fantasyGenerations: settings.button_one.fantasy_generations,
                    })
                }

                // Add button two if configured
                if (settings.button_two) {
                    packageList.push({
                        id: settings.button_two.id,
                        subscriptionId: settings.button_two.subscription_id,
                        name: settings.button_two.name,
                        price: settings.button_two.price,
                        totalPrice: settings.button_two.total_price,
                        billingDetails: settings.button_two.billing_details,
                        pricePerMonth: settings.button_two.price_per_month,
                        savingsText: settings.button_two.savings_text,
                        label: settings.button_two.label,
                        instantCredits: settings.button_two.instant_credits,
                        creditsPerDay: settings.button_two.credits_per_day,
                        fantasyGenerations: settings.button_two.fantasy_generations,
                    })

                    // Default to the second package if it exists (typically the best value)
                    selectedPackage.value = settings.button_two.id
                } else if (settings.button_one) {
                    // Default to first package if only one exists
                    selectedPackage.value = settings.button_one.id
                }

                packages.value = packageList

                if (packageList.length === 0) {
                    error.value = 'No subscription options are configured. Please contact support.'
                }
            } else {
                error.value = 'Unable to load subscription options. Please try again later.'
            }
        } catch (err) {
            console.error('Error fetching subscription settings:', err)
            console.error('Error details:', err.response?.data || err.message)
            error.value =
                err.response?.data?.message ||
                'Unable to load subscription options. Please try again later.'
        } finally {
            isLoading.value = false
        }
    }

    // Watch for modal opening and fetch settings
    watch(
        () => props.isOpen,
        newValue => {
            if (newValue && packages.value.length === 0) {
                fetchSettings()
            }
        },
        { immediate: true },
    )

    const handleSubscribe = async () => {
        const selectedPkg = packages.value.find(pkg => pkg.id === selectedPackage.value)

        if (!selectedPkg) {
            console.error('No package selected')
            return
        }

        isProcessing.value = true

        let characterValues = ''
        if (props.characterId !== -1) {
            characterValues = `?character_id=${props.characterId}&is_custom_character=${props.isCustomCharacter}`
        }

        // Open purchase URL in new tab
        const purchaseUrl = `/purchase/platformSubscription/${selectedPkg.subscriptionId}${characterValues}`

        window.open(purchaseUrl, '_blank')

        // Reset processing state and close modal after a short delay
        setTimeout(() => {
            isProcessing.value = false
            emit('close')
        }, 500)
    }

    const selectedPkg = computed(() => packages.value.find(pkg => pkg.id === selectedPackage.value))

    const onClose = () => {
        emit('close')
    }

    // Calculate token display text
    const tokenText = computed(() => {
        const pkg = selectedPkg.value
        if (!pkg) return 'Includes free tokens instantly'

        if (pkg.instantCredits > 0) {
            return `Includes ${pkg.instantCredits} free tokens instantly`
        }
        return 'Includes free tokens instantly'
    })
</script>

<template>
    <Modal
        :show="isOpen"
        @close="onClose"
        max-width="md"
        :closeable="true"
        :is-blured="true"
        class-name="!bg-[#0b0b0b] !rounded-3xl border border-white/10 !max-w-[420px] sm:!max-w-md ios-modal-content">
        <!-- Close button -->
        <button
            @click="onClose"
            class="absolute right-4 top-4 z-50 rounded-full p-2.5 transition-colors hover:bg-white/10 sm:p-2"
            aria-label="Close">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="h-6 w-6 stroke-[3] text-white sm:h-5 sm:w-5">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
            </svg>
        </button>

        <!-- Loading State -->
        <div v-if="isLoading" class="clear-both py-16 text-center">
            <div
                class="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-[#65e8a4] border-t-transparent"></div>
            <p class="mt-4 text-sm text-neutral-400">Loading subscription options...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="clear-both py-12 text-center">
            <p class="text-sm text-red-400">{{ error }}</p>
            <button
                @click="fetchSettings"
                class="mt-4 rounded-full bg-[#65e8a4] px-6 py-2 text-sm font-semibold text-black transition-colors hover:bg-[#55d894]">
                Try Again
            </button>
        </div>

        <!-- No Packages Configured -->
        <div v-else-if="packages.length === 0" class="clear-both py-12 text-center">
            <p class="text-sm text-neutral-400">No subscription options are currently available.</p>
        </div>

        <!-- Main Content -->
        <template v-else>
            <div class="clear-both">
                <!-- Header -->
                <div class="px-3 pb-5 pt-10 text-center sm:px-6 sm:pb-4 sm:pt-8">
                    <h2 class="mb-4 px-6 text-3xl font-extrabold text-white sm:mb-3 sm:text-2xl">
                        Upgrade to Access
                    </h2>

                    <!-- Token Bonus Badge -->
                    <div
                        class="mb-4 inline-flex items-center gap-2.5 rounded-full border border-[#65e8a4]/30 bg-gradient-to-r from-[#65e8a4]/20 via-[#4bc990]/20 to-[#65e8a4]/20 px-5 py-2.5 sm:mb-3 sm:gap-2 sm:px-4 sm:py-2">
                        <div class="relative flex-shrink-0">
                            <img
                                src="https://aws-lg.naughtycdn.com/mediaAssets/65028/FC_token.png"
                                alt="Token"
                                class="h-5 w-5 sm:h-4 sm:w-4" />
                            <div
                                class="absolute inset-0 animate-pulse rounded-full bg-[#65e8a4]/30 blur-sm"></div>
                        </div>
                        <span class="text-xs font-bold text-[#65e8a4] sm:text-sm">
                            Get a bonus {{ selectedPkg?.instantCredits || 300 }} fantasy bucks for
                            free
                        </span>
                    </div>

                    <p class="text-base leading-relaxed text-neutral-400 sm:text-sm">
                        Get unlimited celebrity voice chats, daily tokens, premium content & custom
                        fantasy creation
                    </p>
                </div>

                <!-- Pricing Pills - Stacked Design -->
                <div class="flex flex-col gap-3.5 px-3 pb-5 sm:gap-3 sm:px-6 sm:pb-4">
                    <button
                        v-for="(pkg, index) in packages"
                        :key="pkg.id"
                        @click="selectedPackage = pkg.id"
                        :class="[
                            'relative rounded-full px-6 py-5 text-left transition-all sm:rounded-full sm:px-5 sm:py-4',
                            selectedPackage === pkg.id
                                ? 'border-2 border-[#65e8a4] bg-gradient-to-r from-[#0f0f0f] to-[#101f16] shadow-lg shadow-[#65e8a4]/30'
                                : 'border border-white/10 bg-[#0f0f0f] hover:border-white/20',
                        ]">
                        <!-- Save Badge -->
                        <span
                            v-if="pkg.savingsText"
                            class="absolute -right-2.5 -top-2.5 rounded-full bg-pink-600 px-3 py-1.5 text-xs font-bold uppercase text-white shadow-md sm:-right-2 sm:-top-2 sm:px-2.5 sm:py-1 sm:text-[10px]">
                            {{ pkg.savingsText }}
                        </span>

                        <div class="flex items-center justify-between">
                            <!-- Left side - Plan name and billing -->
                            <div class="flex flex-col gap-1">
                                <span class="text-lg font-semibold text-white sm:text-base">
                                    {{ pkg.name }}
                                </span>
                                <span class="text-xs text-neutral-400 sm:text-[11px]">
                                    {{ pkg.billingDetails }}
                                </span>
                            </div>

                            <!-- Right side - Price -->
                            <div class="flex items-baseline gap-1">
                                <span class="text-[1.3rem] font-bold text-white">
                                    {{ pkg.pricePerMonth }}
                                </span>
                                <span class="text-sm text-neutral-400 sm:text-xs">/mo</span>
                            </div>
                        </div>
                    </button>
                </div>

                <!-- CTA -->
                <div class="px-6 pb-5 text-center sm:px-6 sm:pb-4">
                    <button
                        @click="handleSubscribe"
                        :disabled="isProcessing"
                        class="w-full rounded-full bg-gradient-to-r from-white to-gray-100 py-5 text-base font-semibold text-gray-800 shadow-md transition-transform hover:scale-[1.03] hover:from-gray-50 hover:to-gray-200 disabled:opacity-50 sm:py-4 sm:text-sm">
                        <span v-if="isProcessing" class="flex items-center justify-center gap-2">
                            <div
                                class="h-4 w-4 animate-spin rounded-full border-2 border-gray-800 border-t-transparent"></div>
                            Processing...
                        </span>
                        <span v-else>
                            Unlock Premium — ${{ selectedPkg?.totalPrice.toFixed(2) }}
                        </span>
                    </button>
                </div>

                <!-- Risk Reversal + Social Proof -->
                <div class="space-y-2 px-6 pb-5 text-center sm:space-y-1 sm:px-6 sm:pb-4">
                    <p class="text-sm text-neutral-400 sm:text-xs">
                        No commitments. Cancel anytime.
                    </p>
                    <div class="flex items-center justify-center gap-1.5 text-amber-400 sm:gap-1">
                        <svg
                            v-for="i in 5"
                            :key="i"
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="h-4 w-4 fill-amber-400 sm:h-3 sm:w-3"
                            aria-hidden="true">
                            <path
                                d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"></path>
                        </svg>
                        <span
                            class="ml-1.5 whitespace-nowrap text-sm text-neutral-400 sm:ml-1 sm:text-xs"
                            >Loved by 50,000+ members</span
                        >
                    </div>
                </div>

                <!-- Value Props -->
                <div class="px-6 pb-8 sm:px-6 sm:pb-8">
                    <div class="rounded-2xl border border-white/10 bg-[#0f0f0f] p-6 sm:p-5">
                        <h3
                            class="mb-3.5 text-base font-semibold text-neutral-300 sm:mb-3 sm:text-sm">
                            What You Get
                        </h3>
                        <ul class="space-y-3 sm:space-y-2">
                            <li
                                class="flex items-start gap-2.5 text-base text-neutral-300 sm:gap-2 sm:text-sm">
                                <CheckCircleIcon class="mt-[4px]" />
                                <span>Unlimited fantasy chat with celebrity voices</span>
                            </li>

                            <li
                                class="flex items-start gap-2.5 text-base text-neutral-300 sm:gap-2 sm:text-sm">
                                <CheckCircleIcon class="mt-[4px]" />
                                <span>
                                    {{ selectedPkg?.instantCredits || 300 }} welcome tokens +
                                    {{ selectedPkg?.creditsPerDay || 10 }} daily free tokens
                                </span>
                            </li>

                            <li
                                class="flex items-start gap-2.5 text-base text-neutral-300 sm:gap-2 sm:text-sm">
                                <CheckCircleIcon class="mt-[4px]" />
                                <span>Access to premium images, videos & content</span>
                            </li>

                            <li
                                class="flex items-start gap-2.5 text-base text-neutral-300 sm:gap-2 sm:text-sm">
                                <CheckCircleIcon class="mt-[4px]" />
                                <span>Generate your own fantasies & characters</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </template>
    </Modal>
</template>

<style>
    /* Fix iOS scrolling - using non-scoped to override Modal component styles */
    .ios-modal-content {
        /* Set max height to ensure scrollability on small devices */
        max-height: 85vh !important;

        /* Enable overflow scrolling on the modal content itself */
        overflow-y: auto !important;

        /* Enable iOS momentum scrolling */
        -webkit-overflow-scrolling: touch !important;
        touch-action: pan-y !important;
        overscroll-behavior: contain !important;
    }

    /* Ensure the parent modal container can scroll */
    #staticModal {
        -webkit-overflow-scrolling: touch !important;
        touch-action: pan-y !important;
        overscroll-behavior: contain !important;
    }
</style>
